/*Global Dependencies*/
import {useState , useEffect} from "react";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { Spin } from 'antd';
import {map} from 'lodash';

/*Local Dependencies*/
import Header from "./header/header";
import Filter from "./filter/filter";
import TableTabs from "./table-tabs/tableTabs";
import "./highCostsPatientReport.scss";
import HighCostPatientService from "@services/high-cost-patient.service";
import { useHighCostPatientStore } from "@stores/highCostPatientStore";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import { ellipsis } from "@utils/helpers";

const HighCostPatient = () => {

  const paginationFilterValue = useHighCostPatientStore((state) => state.paginationFilterValue);
  const setPaginationFilterValue = useHighCostPatientStore((state) => state.setPaginationFilterValue);
  const refreshDate = useHighCostPatientStore((state) => state.refreshDate);
  const setRefreshDate = useHighCostPatientStore((state) => state.setRefreshDate);
  const selectedOffice = useHighCostPatientStore((state) => state.selectedOffice);
  const setSelectedOffice = useHighCostPatientStore((state) => state.setSelectedOffice);
  const selectedInsurance = useHighCostPatientStore((state) => state.selectedInsurance);
  const setSelectedInsurance = useHighCostPatientStore((state) => state.setSelectedInsurance);
  const selectedRegion = useHighCostPatientStore((state) => state.selectedRegion);
  const setSelectedRegion = useHighCostPatientStore((state) => state.setSelectedRegion);
  const buildDone = useHighCostPatientStore((state) => state.buildDone);
  const setBuildDone = useHighCostPatientStore((state) => state.setBuildDone);
  const [loader , setLoader] = useState<boolean>(false);
  const [tableData , setTableData] = useState<any>({"Part A" : [] , "Part B" : [] , "Part D" : [] , "Total" : []});
  const [totalCount , setTotalCount] = useState<number>(0);
  const rowsPerPage = useHighCostPatientStore((state) => state.rowsPerPage);
  const currentPage = useHighCostPatientStore((state) => state.currentPage);
  const setCurrentPage = useHighCostPatientStore((state) => state.setCurrentPage);
  const activeTab= useHighCostPatientStore((state) => state.activeTab);
  const sortData = useHighCostPatientStore((state) => state.sortData);
  const searchKeywordNew= useHighCostPatientStore((state) => state.searchKeywordNew);
  const [tableLoader , setTableLoader] = useState<boolean>(false);
  const setSelectedRecord = useHighCostPatientStore((state) => state.setSelectedRecord);
  const setShowModel = useHighCostPatientStore((state) => state.setShowModel);
  const columnData= useHighCostPatientStore((state) => state.columnData);
  const setColumnData = useHighCostPatientStore((state) => state.setColumnData);
  const [dropdownValueChanged , setDropdownValueChanged] = useState<boolean>(false);
  const [initialBuild , setInitialBuild] = useState<boolean>(false);
  const location : any = useLocation();

  useEffect(() => {
    console.log("HighCostPatientReport.useEffect1")
    //to fetch the refresh date when navigated to high cost patient for the first time 
    if(!location?.state?.apiCallNotNeeded || !refreshDate) {
      const fetchRefreshDate = () => {
        HighCostPatientService.getRefreshDate().subscribe({
          next: (response: any) => {
            setRefreshDate(moment.utc(response.data.yearMonth));
          },
          error: (error) => {
            console.log(error);
          },
        });
      }
      fetchRefreshDate();
      getColumns();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  //to get the table data for each tab
  const getTableData = ({topVal = paginationFilterValue !== "ALL", limit = rowsPerPage, page = currentPage[activeTab.expenseType], expense = activeTab.expenseType , sortBy = sortData[activeTab.expenseType].sortBy , sortType = sortData[activeTab.expenseType].sortType , searchOn = searchKeywordNew[activeTab.expenseType]}) => {
    console.log("genExpense.getTableData");
    setTableLoader(true);
    HighCostPatientService.getTableData({topVal , limit , page , expense , sortBy , sortType , searchOn}).subscribe({
      next: (response: any) => {
        setTotalCount(response?.data?.data?.count);
        setTableData({...tableData , [expense] : response?.data?.data?.patients});
        setTableLoader(false);
      },
      error: (error) => {
        console.log(error);
        setTableLoader(false);
      },
    });
  }

  //to get the columns data once and keep it same for all the tabs and keep current rank ascend as default sort 
  const getColumns = () => {
    console.log("TableTabs.getColumns");
    //to call the columns api 
    HighCostPatientService.getColumnsList().subscribe({
      next: (response: any) => {
        const data = map(response?.data?.data?.columns , (item : any) => {
          item.sequence = item.order;
          item.label = item.displayName;
          item.title = item.tableHeaderName;
          if(item.dataIndex !== "notes"){
            item.sorter = () => {};
          }
          if(item.dataIndex === 'expense_amt'){
            item.width = 160;
          }
          if(item.dataIndex === 'current_rank'){
            item.sortOrder = 'ascend';
          }
          if(item.dataIndex === 'notes'){
            //to make notes clickable to open notes modal
            item.render = (text : string, record : any) => {
              return (
                <div className="notes_cell">{record.notes?.includes('$ON$_') ? ellipsis(record.notes.split('$ON$_')[1] , 10) : record.notes}
                <span
                  className="icon-edit f18 clr-blueLink pointer"
                  onClick={() => {
                    setSelectedRecord(record);
                    setShowModel(true);
                  }}
                /></div>
              );
            }
          };
          return item;
        })
        setColumnData({"Part A" : data , "Part B" : data , "Part D" : data , "Total" : data});
      },
      error: (error) => {
        console.log(error);
      },
    });
  } 

  return (
    <div className="high_cost_report">
      <Spin
        spinning={loader}
        indicator={
          <HealthemSpinner
            size={60}
            progressBarNeeded={true}
            progressData={[
              { progress: 30, message: "Creating Dataset" },
              { progress: 60, message: "Calculating Ranks" },
              { progress: 90, message: "Please wait, you are almost there!" },
            ]}
            timeLimit={process.env.REACT_APP_HIGH_COST_PATIENT_BUILD_TIME_LIMIT ? Number(process.env.REACT_APP_HIGH_COST_PATIENT_BUILD_TIME_LIMIT) : 2000}
          />
        }
      >
        <Header refreshDate={refreshDate} setRefreshDate={setRefreshDate} setDropdownValueChanged={setDropdownValueChanged} setInitialBuild={setInitialBuild}/>
        <Filter
          selectedOffice={selectedOffice}
          setSelectedOffice={setSelectedOffice}
          refreshDate={refreshDate}
          paginationFilterValue={paginationFilterValue}
          setPaginationFilterValue={setPaginationFilterValue}
          selectedInsurance={selectedInsurance}
          setSelectedInsurance={setSelectedInsurance}
          selectedRegion = {selectedRegion}
          setSelectedRegion = {setSelectedRegion}
          setBuildDone={setBuildDone}
          setLoader={setLoader}
          getTableData={getTableData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          activeTab={activeTab}
          dropdownValueChanged={dropdownValueChanged}
          setDropdownValueChanged={setDropdownValueChanged}
          initialBuild={initialBuild}
          setInitialBuild={setInitialBuild}
        />
        <TableTabs refreshDate={refreshDate} top10={paginationFilterValue !== "ALL"} buildDone={buildDone}  tableData={tableData} setTableData={setTableData} totalCount={totalCount} setTotalCount={setTotalCount} tableLoader={tableLoader} setTableLoader={setTableLoader} getTableData={getTableData} columnData = {columnData}/>
      </Spin>
    </div>
  );
};

export default HighCostPatient;
